// src/config/api.config.js
// Standalone: NO imports aquí (evita circular deps y TDZ)

const isBrowser = typeof window !== "undefined";

const viteIsDev =
  typeof import.meta !== "undefined" &&
  import.meta.env &&
  typeof import.meta.env.DEV === "boolean"
    ? import.meta.env.DEV
    : false;

const nodeIsDev =
  typeof process !== "undefined" &&
  process.env &&
  process.env.NODE_ENV === "development";

const isDevelopment = viteIsDev || nodeIsDev;

// En producción debe ser el dominio SIN /api
// Ej: VITE_API_URL=https://desarrollandoterritorios.com/
const RAW_BASE_URL =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_URL) ||
  "";

// ✅ Normaliza: sin slash final y SIN /api al final
const BASE_URL = String(RAW_BASE_URL)
  .trim()
  .replace(/\/+$/, "")
  .replace(/\/api$/i, "");

// ✅ Si NO hay VITE_API_URL, usa SIEMPRE same-origin (sirve en prod y evita localhost raro)
const EFFECTIVE_BASE_URL = BASE_URL || (isBrowser ? window.location.origin : "");

/** Une base + path de forma segura (NUNCA revienta la app) */
function joinUrl(base, path) {
  const fallback = "/api/ping.php";

  if (typeof path !== "string" || !path.trim()) {
    console.error("❌ joinUrl(): path inválido:", { path });
    path = fallback;
  }

  const p = path.startsWith("/") ? path : `/${path}`;
  if (!base) return p; // same-origin
  return `${base}${p}`;
}

/**
 * getUrl(path, idOrQuery)
 * - path: "/api/...."
 * - idOrQuery: "id=123" o "foo=bar"
 *
 * ✅ Diagnóstico: si path llega undefined o no-string, lo loguea con stack y NO revienta la app.
 */
export function getUrl(path, idOrQuery) {
  if (typeof path !== "string" || !path.trim()) {
    const err = new Error("getUrl(): path inválido (endpoint mal definido)");
    console.error("❌ getUrl recibió path inválido:", { path, idOrQuery });
    console.error("❌ Stack trace:", err.stack);
    path = "/api/ping.php";
  }

  let finalPath = path;

  if (typeof idOrQuery === "string" && idOrQuery.trim()) {
    finalPath += finalPath.includes("?") ? `&${idOrQuery}` : `?${idOrQuery}`;
  }

  return joinUrl(EFFECTIVE_BASE_URL, finalPath);
}

// Objeto central de API
export const apiConfig = {
  isDevelopment,
  baseUrl: EFFECTIVE_BASE_URL,

  endpoints: {
    // --- Base / health ---
    ping: "/api/ping.php",

    // --- Auth ---
    login: "/api/auth/login/index.php",
    logout: "/api/auth/logout/index.php",
    me: "/api/auth/me/index.php",

    // --- Contracts ---
    contracts: "/api/contracts/index.php",
    contractsCreate: "/api/contracts/create/index.php",
    contractsAdvance: "/api/contracts/advance/index.php",
    contractsUpdate: "/api/contracts/update/index.php",
    contractHistory: "/api/contracts/history/index.php", // ✅ historial de movimientos

    // --- Notificaciones ---
    notifications: "/api/notifications/index.php",

    // --- Firmas ---
    uploadFirma: "/api/firmas/upload/index.php",

    // --- CDP / RP formularios ---
    cdpCreate: "/api/cdp/create/index.php",
    cdpEnviar: "/api/cdp/enviar/index.php",
    cdpFormularios: "/api/cdp/formularios/index.php",

    rpCreate: "/api/rp/create/index.php",
    rpEnviar: "/api/rp/enviar/index.php",

    // ✅ GET (listar/consultar RP por contractId)
    rpFormularios: "/api/rp/formularios/index.php",

    // ✅ POST (crear/guardar RP)
    rpFormulariosCreate: "/api/rp/formularios/create/index.php",

    // =========================================================
    // ✅ PRESUPUESTO (solicitudes CDP/RP) - UNIFICADO
    // =========================================================
    presupuestoRequests: "/api/presupuesto/solicitudes/index.php",
    presupuestoSolicitudes: "/api/presupuesto/solicitudes/index.php",
    presupuestoResponder: "/api/presupuesto/solicitudes/responder/index.php",
    presupuestoAllPending: "/api/presupuesto/solicitudes/all-pending.php",
    uploadPresupuestoAttachment: "/api/presupuesto/solicitudes/upload/index.php",

    // =========================================================
    // ✅ ALMACÉN
    // =========================================================
    almacenSolicitudes: "/api/almacen/solicitudes/index.php",
    almacenResponder: "/api/almacen/solicitudes/responder/index.php",
    uploadAlmacenAttachment: "/api/almacen/solicitudes/upload/index.php",
    almacenSolicitudesByContract: "/api/almacen/solicitudes/index.php",

    // =========================================================
    // ✅ BIENES / ADQUISICIÓN
    // =========================================================
    bienesSolicitudes: "/api/adquisicion/solicitudes/index.php",
    bienesResponder: "/api/adquisicion/solicitudes/responder/index.php",
    uploadBienesAttachment: "/api/adquisicion/solicitudes/upload/index.php",

    // =========================================================
    // ✅ INEXISTENCIA
    // =========================================================
    inexistenciaSolicitudes: "/api/inexistencia/solicitudes/index.php",
    inexistenciaResponder: "/api/inexistencia/solicitudes/respond/index.php",
    uploadInexistenciaAttachment: "/api/inexistencia/solicitudes/upload/index.php",
    inexistenciaByContract: "/api/inexistencia/by-contract/index.php",

    // =========================================================
    // ✅ EXPORTS (para evitar hardcode en componentes)
    // =========================================================
    exportDb: "/api/export/db.php",
    exportAnalitica: "/api/export/analitica_contratacion.php",
  },

  // Helper universal
  getUrl(path, idOrQuery) {
    return getUrl(path, idOrQuery);
  },

  routes: {
    ping() {
      return apiConfig.getUrl(apiConfig.endpoints.ping);
    },

    login() {
      return apiConfig.getUrl(apiConfig.endpoints.login);
    },

    logout() {
      return apiConfig.getUrl(apiConfig.endpoints.logout);
    },

    me() {
      return apiConfig.getUrl(apiConfig.endpoints.me);
    },

    // Obtener contrato
    contractGet(id) {
      if (!id) throw new Error("contractGet(id): id requerido");

      if (isDevelopment) {
        // dev proxy /api/contracts/:id
        return apiConfig.getUrl(`/api/contracts/${encodeURIComponent(id)}`);
      }

      // prod php /api/contracts/index.php?id=...
      return apiConfig.getUrl(apiConfig.endpoints.contracts, `id=${encodeURIComponent(id)}`);
    },

    contractsList() {
      return apiConfig.getUrl(apiConfig.endpoints.contracts);
    },

    contractsCreate() {
      if (isDevelopment) return apiConfig.getUrl(`/api/contracts`);
      return apiConfig.getUrl(apiConfig.endpoints.contractsCreate);
    },

    contractsUpdate() {
      return apiConfig.getUrl(apiConfig.endpoints.contractsUpdate);
    },

    contractHistory() {
      return apiConfig.getUrl(apiConfig.endpoints.contractHistory);
    },

    advance() {
      if (isDevelopment) return apiConfig.getUrl(`/api/contracts/advance`);
      return apiConfig.getUrl(apiConfig.endpoints.contractsAdvance);
    },

    notifications() {
      return apiConfig.getUrl(apiConfig.endpoints.notifications);
    },

    uploadFirma() {
      return apiConfig.getUrl(apiConfig.endpoints.uploadFirma);
    },

    // CDP/RP create/enviar
    cdpCreate() {
      return apiConfig.getUrl(apiConfig.endpoints.cdpCreate);
    },
    cdpEnviar() {
      return apiConfig.getUrl(apiConfig.endpoints.cdpEnviar);
    },
    rpCreate() {
      return apiConfig.getUrl(apiConfig.endpoints.rpCreate);
    },
    rpEnviar() {
      return apiConfig.getUrl(apiConfig.endpoints.rpEnviar);
    },

    // Formularios
    cdpFormularios() {
      return apiConfig.getUrl(apiConfig.endpoints.cdpFormularios);
    },

    rpFormularios() {
      return apiConfig.getUrl(apiConfig.endpoints.rpFormularios);
    },

    rpFormulariosCreate() {
      return apiConfig.getUrl(apiConfig.endpoints.rpFormulariosCreate);
    },

    // Presupuesto
    presupuestoRequests() {
      return apiConfig.getUrl(apiConfig.endpoints.presupuestoRequests);
    },
    presupuestoResponder() {
      return apiConfig.getUrl(apiConfig.endpoints.presupuestoResponder);
    },
    uploadPresupuestoAttachment() {
      return apiConfig.getUrl(apiConfig.endpoints.uploadPresupuestoAttachment);
    },

    // Almacén
    almacenSolicitudes() {
      return apiConfig.getUrl(apiConfig.endpoints.almacenSolicitudes);
    },
    almacenResponder() {
      return apiConfig.getUrl(apiConfig.endpoints.almacenResponder);
    },
    uploadAlmacenAttachment() {
      return apiConfig.getUrl(apiConfig.endpoints.uploadAlmacenAttachment);
    },
    almacenSolicitudesByContract() {
      return apiConfig.getUrl(apiConfig.endpoints.almacenSolicitudesByContract);
    },

    // Bienes
    bienesSolicitudes() {
      return apiConfig.getUrl(apiConfig.endpoints.bienesSolicitudes);
    },
    bienesResponder() {
      return apiConfig.getUrl(apiConfig.endpoints.bienesResponder);
    },
    uploadBienesAttachment() {
      return apiConfig.getUrl(apiConfig.endpoints.uploadBienesAttachment);
    },

    // Inexistencia
    inexistenciaSolicitudes() {
      return apiConfig.getUrl(apiConfig.endpoints.inexistenciaSolicitudes);
    },
    inexistenciaResponder() {
      return apiConfig.getUrl(apiConfig.endpoints.inexistenciaResponder);
    },
    uploadInexistenciaAttachment() {
      return apiConfig.getUrl(apiConfig.endpoints.uploadInexistenciaAttachment);
    },
    inexistenciaByContract() {
      return apiConfig.getUrl(apiConfig.endpoints.inexistenciaByContract);
    },

    // Exports
    exportDb(role) {
      const q = role ? `role=${encodeURIComponent(role)}` : "";
      return apiConfig.getUrl(apiConfig.endpoints.exportDb, q);
    },
    exportAnalitica(role) {
      const q = role ? `role=${encodeURIComponent(role)}` : "";
      return apiConfig.getUrl(apiConfig.endpoints.exportAnalitica, q);
    },
  },
};

// Debug controlado
if (isBrowser) {
  try {
    console.log(
      "API Config initialized for",
      apiConfig.isDevelopment ? "DEVELOPMENT" : "PRODUCTION"
    );
    console.log("Base URL:", apiConfig.baseUrl || "(same origin)");
  } catch (_) {}
}